import React from 'react';
import { Flex, Heading, Text, Image } from 'rebass';
import Frame from 'components/Frame';
import { RouteComponentProps } from 'react-router';
import { UserRoleType } from 'types/global';
import { RECRUITER, EMPLOYER } from 'constants/index';
import useGetJobPosts from 'modules/JobPosts/hooks/useGetJobPosts';
import JobPostsNavigation from 'modules/JobPosts/components/JobPostsNavigation';
import JobPost from 'modules/JobPosts/components/JobPost';
import LackOfJobPosts from 'modules/JobPosts/components/LackOfJobPosts';
import JobPostingDropDown from 'modules/JobPosts/components/JobPostingDropDown';
import useDropDownHandlers from 'hooks/useDropDownHandlers';
import useSorting from 'modules/JobPosts/hooks/useSorting';
import arrow from 'assets/icons/arrow.png';
import useModal from 'hooks/useModal';

interface JobPostsProps {
  userRole: UserRoleType;
}

const JobPosts: React.FC<RouteComponentProps & JobPostsProps> = (props): JSX.Element => {
  const { userRole, match } = props;
  const {
    allJobPosts,
    userJobPosts,
    savedJobPosts,
    jobPostsWithCandidates,
    hasJobPosts,
  } = useGetJobPosts();
  const { isDropDownVisible: dropDownName, openDropDown, closeDropDown } = useDropDownHandlers();

  const { sortValue, setSortValue, typeValue, setTypeValue } = useSorting();
  const { openModalHandler } = useModal();
  return (
    <>
      <Flex>
        <Frame>
          <Flex width="100%" flexDirection="column" pt={7} pb={1}>
            <JobPostsNavigation userRole={userRole} hasJobPosts={hasJobPosts} url={match.url} />
            {userRole === RECRUITER && match.url === '/job-posts/all' && hasJobPosts ? (
              <Flex width="100%" alignItems="center" justifyContent="space-between" mb={5}>
                <Flex>
                  <JobPostingDropDown
                    isDropDownVisible={dropDownName === 'type'}
                    openDropDown={() => openDropDown('type')}
                    closeDropDown={closeDropDown}
                    values={['All', 'Permanent', 'Part time']}
                    onChange={newValue => setTypeValue(newValue)}
                    currentValue={typeValue}
                    title="Job Type"
                    contentStyle={{ left: 0, top: '100%' }}
                    buttonStyle={{ marginRight: 20 }}
                  />
                  <Flex
                    alignItems="center"
                    onClick={openModalHandler({
                      title: 'searchByLocation',
                      data: {},
                    })}
                    css="cursor: pointer"
                  >
                    <Text fontSize="18px" fontWeight={5}>
                      Location
                    </Text>
                    <Image src={arrow} ml={1} />
                  </Flex>
                </Flex>
                <Flex justifyContent="flex-end">
                  <JobPostingDropDown
                    isDropDownVisible={dropDownName === 'sort'}
                    openDropDown={() => openDropDown('sort')}
                    closeDropDown={closeDropDown}
                    values={['Newest', 'Oldest', 'A - Z']}
                    onChange={newValue => setSortValue(newValue)}
                    currentValue={sortValue}
                    title={`Sort by ${sortValue}`}
                    contentStyle={{ right: 0, top: '100%' }}
                  />
                </Flex>
              </Flex>
            ) : null}
            <Flex width="100%" justifyContent="space-between" flexWrap="wrap">
              {(() => {
                switch (userRole) {
                  case RECRUITER:
                    if (match.url === '/job-posts/all' && !!allJobPosts.length) {
                      return allJobPosts.map((post: any) => {
                        return (
                          <JobPost key={post.id} post={post} userRole={userRole} url={match.url} />
                        );
                      });
                    }
                    if (
                      match.url === '/job-posts' &&
                      (!!jobPostsWithCandidates.length || !!savedJobPosts.length)
                    ) {
                      return (
                        <>
                          {!!jobPostsWithCandidates.length && (
                            <Flex width="100%" flexDirection="column">
                              <Heading fontSize="24px" fontWeight={5} mb={3}>
                                Active
                              </Heading>
                              <Flex width="100%" justifyContent="space-between" flexWrap="wrap">
                                {jobPostsWithCandidates.map((post: any) => (
                                  <JobPost
                                    key={post.id}
                                    post={post}
                                    userRole={userRole}
                                    url={match.url}
                                  />
                                ))}
                              </Flex>
                            </Flex>
                          )}
                          {!!savedJobPosts.length && (
                            <Flex width="100%" flexDirection="column">
                              <Heading fontSize="24px" fontWeight={5} mb={3}>
                                Saved for Later
                              </Heading>
                              <Flex width="100%" justifyContent="space-between" flexWrap="wrap">
                                {savedJobPosts.map((post: any) => (
                                  <JobPost
                                    key={post.id}
                                    post={post}
                                    userRole={userRole}
                                    url={match.url}
                                  />
                                ))}
                              </Flex>
                            </Flex>
                          )}
                        </>
                      );
                    }
                    return <LackOfJobPosts userRole={userRole} />;
                  case EMPLOYER:
                    if (userJobPosts.length) {
                      return userJobPosts.map((post: any) => (
                        <JobPost key={post.id} post={post} userRole={userRole} url={match.url} />
                      ));
                    }
                    return <LackOfJobPosts userRole={userRole} />;
                  default:
                    return null;
                }
              })()}
            </Flex>
          </Flex>
        </Frame>
      </Flex>
    </>
  );
};

export default JobPosts;
